import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PipeTransform,
  TemplateRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseDto } from '@authority-labs/api-interfaces';
import { PERMISSION_KEYS, PermissionKey } from '@authority-labs/constants';
import { Path } from '@authority-labs/enums';
import { CrudDialog } from '@check-list/app/classes/crud-dialog';
import {
  Action,
  ColumnFilter,
  TableColumn
} from '@check-list/app/classes/models/';
import { ActionTableGroup } from '@check-list/core/consts/action-table-group';
import { MenuItemsData } from '@check-list/core/consts/menu-items-data.const';
import { ChangeType } from '@check-list/core/enums/change-type.enum';
import { CrudItemChanges } from '@check-list/core/models/crud-changes.model';
import { AuthenticationService } from '@check-list/app/auth/authentication.service';
import { AppPermissionsService } from '@check-list/shared/services/permissions/permissions.service';
import { StorageService } from '@check-list/shared/services/storage/storage.service';
import {
  CondOperator,
  QueryFilter,
  QueryJoin,
  QuerySort,
  QuerySortOperator,
  RequestQueryBuilder
} from '@nestjsx/crud-request';
import { TranslocoService } from '@ngneat/transloco';
import { endOfDay, startOfDay } from 'date-fns';
import { isArray } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService } from '../../../modules/users/users.service';
import { NzModalConfirmActionComponent } from '../../dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { UsernamePipe } from '../../pipes/username/username.pipe';
import { CrudService } from '../../services/crud/crud.service';
import { LayoutService } from '../../services/layout';

const LIMIT = 20;
const COLORS = [
  'magenta',
  'red',
  'orange',
  'gold',
  'lime',
  'green',
  'cyan',
  'blue',
  'purple'
];

@Component({
  selector: 'authority-labs-crud-table',
  templateUrl: './crud-table.component.html',
  styleUrls: ['./crud-table.component.scss']
})
export class CrudTableComponent<T extends BaseDto, S extends CrudService<T>>
  implements OnInit, OnDestroy
{
  @Input() title: string;
  @Input() crudService: S;
  @Input() columns: TableColumn[] = [];
  @Input() actionColumn = ActionTableGroup.Show;
  @Input() crudDialog: CrudDialog<T>;
  @Input() rows: (T & { expand?: boolean })[] = [];
  @Input() simpleTable = false;
  @Input() hideTitle = false;
  @Input() isSelectable = false;
  @Input() hideActionsColumn = false;
  @Input() autoHeight = false;
  @Input() pageLink: Path;
  @Input() parent: string;
  @Input() parentId: number;
  @Input() exportedColumns: string[] = [];
  @Input() exportPermission: PermissionKey;
  @Input() disableDefaultColumns = false;
  @Input() extraParams: QueryJoin[];
  @Input() filterParams: QueryFilter[];
  @Input() uploadDirectories: string[] = [];
  @Input() isDialog = false;
  @Input() expandedRowTemplate: TemplateRef<unknown>;
  @Input() customSortFn: (
    x: (T & { expand?: boolean })[]
  ) => (T & { expand?: boolean })[];
  @Input() customDeleteErrorHandler?: (error: unknown) => boolean;
  @Output() dataChange: EventEmitter<T[]> = new EventEmitter<T[]>();
  @Output() menuTrigger: EventEmitter<string> = new EventEmitter<string>();
  @Output() rowSelected: EventEmitter<T> = new EventEmitter<T>();

  isLoading = true;
  totalElements = 1;
  pageNumber = 1;
  limit: number = LIMIT;
  filtering = false;
  sorts = [];
  isMobile = false;
  isScrollable: { x: string; y: string };
  dateFormat = 'yyyy/MM/dd';
  aggregatedData: { [P in keyof T]?: number };
  selectedColumn: TableColumn;
  expand = false;
  selectedRow: T;

  actionsMap = new Map<Action, (item: unknown) => void>();

  private colorsMap: Map<string, string> = new Map();
  private qb: RequestQueryBuilder;
  private componentDestroyed$: Subject<boolean> = new Subject();
  private readonly columnDefaults: TableColumn = {
    type: 'plainText',
    fieldType: 'text',
    sortDirection: ['ascend', 'descend'],
    isVisible: true
  };

  get aggregatedDataKey(): string {
    const keys = Object.keys(this.aggregatedData);
    return keys ? keys[0] : '';
  }

  get aggregatedDataValue(): string {
    const keys = Object.keys(this.aggregatedData);
    return keys ? this.aggregatedData[keys[0]] : '';
  }

  constructor(
    private usersService: UsersService,
    private modal: NzModalService,
    private message: NzMessageService,
    private layoutService: LayoutService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthenticationService,
    private appPermissionsService: AppPermissionsService,
    private storageService: StorageService,
    private translocoService: TranslocoService,
    private usernamePipe: UsernamePipe,
    private datePipe: DatePipe,
    private cdRef: ChangeDetectorRef
  ) {
    this.pageNumber = 0;
    this.qb = RequestQueryBuilder.create({
      limit: LIMIT
    });

    this.actionsMap.set(MenuItemsData.DELETE, this.deleteItem.bind(this));
    this.actionsMap.set(
      MenuItemsData.UPLOAD,
      this.uploadToItemStorage.bind(this)
    );

    this.actionsMap.set(
      MenuItemsData.OPEN_STORAGE,
      this.openItemStorage.bind(this)
    );
    this.actionsMap.set(MenuItemsData.SHOW, this.openItem.bind(this));

    CrudService.changesEmitter
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((event) => this.changesHandler(event));
  }

  ngOnInit(): void {
    if (!this.disableDefaultColumns) {
      let updatedAtColumn: TableColumn = this.columns.find(
        (c) => c.prop === 'updatedAt' && c.fieldType === 'date'
      );

      const createdAtColumn: TableColumn = {
        prop: 'createdAt',
        type: 'pipe',
        pipe: this.datePipe,
        fieldType: 'date'
      };
      const createdByColumn: TableColumn = {
        prop: 'createdBy',
        type: 'pipe',
        pipe: this.usernamePipe,
        fieldType: 'dropdown',
        filters: this.usersService.usersFilters
      };
      const updatedByColumn: TableColumn = {
        prop: 'updatedBy',
        type: 'pipe',
        pipe: this.usernamePipe,
        fieldType: 'dropdown',
        filters: this.usersService.usersFilters
      };

      if (updatedAtColumn) {
        updatedAtColumn.type = 'pipe';
        updatedAtColumn.pipe = this.datePipe;
        this.columns = [
          ...this.columns,
          createdAtColumn,
          createdByColumn,
          updatedByColumn
        ];
      } else {
        updatedAtColumn = {
          prop: 'updatedAt',
          type: 'pipe',
          pipe: this.datePipe,
          fieldType: 'date'
        };
        this.columns = [
          ...this.columns,
          createdAtColumn,
          createdByColumn,
          updatedAtColumn,
          updatedByColumn
        ];
      }
    }

    this.totalElements = this.rows ? this.rows.length : 0;

    this.columnDefaults.sortDirection = ['ascend', 'descend'];
    this.columns = this.columns.map((column) => ({
      ...this.columnDefaults,
      ...column
    }));

    this.layoutService.isMobile.subscribe(
      (isMobile) => (this.isMobile = isMobile)
    );

    this.setColors();
    this.setTableWidth();
    this.loadCustomizedData();
    this.loadAppliedSort();
    this.loadAppliedFilters();
    this.checkQueryParams();
    this._setAndFetchByFilters();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getColumnColor(column: TableColumn): string {
    return this.colorsMap.get(this.getColumnProp(column));
  }

  isUsernamePipe(pipe: PipeTransform): boolean {
    return pipe instanceof UsernamePipe;
  }

  isUsernameColumn(column: TableColumn): boolean {
    return (
      column.type === 'pipe' && column.pipe && this.isUsernamePipe(column.pipe)
    );
  }

  isDateColumn(column: TableColumn): boolean {
    return column.fieldType === 'date';
  }

  displayLink(routes: string[] = [], value: string): string[] {
    return [...routes, value];
  }

  displayCustomLink(item: T, generateLinkFn: (obj: T) => string[]): string[] {
    return generateLinkFn(item);
  }

  createItem(): void {
    this.crudDialog.create();
  }

  async uploadToItemStorage(item: {
    id: number;
    name: string;
    sharePoint?: Record<string, string>;
  }): Promise<void> {
    if (!(item.sharePoint && item.sharePoint.id)) {
      const qb = RequestQueryBuilder.create({
        join: { field: 'sharePoint' }
      });
      const obj = await firstValueFrom(
        this.crudService.getOne(item.id, qb.query())
      );

      item.sharePoint = (
        obj as T & { sharePoint?: Record<string, string> }
      ).sharePoint;
    }
    const directories = {};

    this.uploadDirectories.forEach((key) => {
      directories[key] = item.sharePoint?.[key];
    });
    this.storageService.openUploadWindow(directories, {
      id: item.id,
      name: item.name,
      type: this.pageLink
    });
  }

  openItemStorage(item: {
    id: number;
    name: string;
    sharePoint?: { id: string };
  }): void {
    if (item.sharePoint && item.sharePoint.id) {
      this.storageService.storageNavigate(item.sharePoint.id);
    } else {
      this.openErrMessage('error.unknownErr');
    }
  }

  openErrMessage(errKey: string) {
    this.translocoService.selectTranslate(errKey).subscribe((translate) => {
      this.message.error(translate);
    });
  }

  deleteItem(item: T): void {
    const subTitle = `האם את/ה בטוח/ה שברצונך למחוק את "${
      item.name || item.id
    }"?`;

    this.modal.create({
      nzClosable: false,
      nzContent: NzModalConfirmActionComponent,
      nzComponentParams: {
        subTitle
      },
      nzOnOk: () => this.deleteOne(item.id)
    });
  }

  getAvatar(username: string): string {
    const user = this.usersService.users.find((u) => u.username === username);
    return user ? user.avatar : '';
  }

  setPage(pageNumber: number): void {
    this.pageNumber = pageNumber;

    const query = this.qb.setPage(pageNumber);

    this.fetch(query);
  }

  applySort(direction: 'ascend' | 'descend', column: TableColumn): void {
    if (direction && column) {
      const querySort = {
        field: this.getColumnProp(column),
        order: this.sortOrder(direction)
      };

      this.storeAppliedSort(querySort);

      this.qb.queryObject.sort = null;
      const query = this.qb.setLimit(this.limit).setPage(1).sortBy(querySort);
      this.fetch(query);
    }
  }

  applyFilter(filters: ColumnFilter[] = [], column: TableColumn): void {
    column.appliedFilters = filters.length === 0 ? null : filters;
    this._setAndFetchByFilters();
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex, sort } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const sortField = (currentSort && currentSort.key) || null;
    const sortOrder = (currentSort && currentSort.value) || null;

    if (pageIndex > 0) {
      const prevQuery = this.qb.query();

      this.qb.queryObject.sort = null;
      this.qb.setLimit(this.limit).setPage(pageIndex);

      if (sortField && sortOrder) {
        const querySort = {
          field: sortField,
          order: this.sortOrder(sortOrder)
        };

        this.qb.sortBy(querySort);
      }

      const curQuery = this.qb.query();
      if (prevQuery !== curQuery) {
        this.fetch(this.qb);
      }
    }
  }

  search(column: TableColumn) {
    if (column.appliedFilters?.length > 0) {
      this.selectedColumn.isSearchVisible = false;
      this.selectedColumn = null;
      column.isFilterApplied = true;
      this._setAndFetchByFilters();
    } else {
      this.onRemoveTag(column);
    }
  }

  onSearchClick(column: TableColumn, isVisible: boolean) {
    this.selectedColumn = isVisible ? column : null;
    column.isSearchVisible = isVisible;
  }

  onRemoveTag(column: TableColumn) {
    if (this.selectedColumn) {
      this.selectedColumn.isSearchVisible = false;
      this.selectedColumn = null;
    }
    column.appliedFilters = null;
    column.isFilterApplied = false;
    this._setAndFetchByFilters();
  }

  menuClick(menuItem: Action, item: T) {
    if (menuItem.fn) {
      menuItem.fn(item);
    } else {
      this.actionsMap.get(menuItem)(item);
    }
  }

  getColumnProp(column: TableColumn): string {
    if (column.alias) {
      return `${column.alias}.${column.objectField}`;
    }

    return (column.type === 'object' ||
      column.type === 'template' ||
      column.type === 'pipe') &&
      column.objectField
      ? `${column.prop}.${column.objectField}`
      : column.prop;
  }

  getColumnObjValue(obj: T, path: string): string | undefined {
    try {
      const value =
        obj && path ? path.split('.').reduce((v, el) => v && v[el], obj) : '';
      return value;
    } catch (err) {
      console.error(err);
    }
  }

  isEqualsFilter(column: TableColumn): boolean {
    return (
      this.isUsernameFilter(column) ||
      this.isBooleanFilter(column) ||
      this.isNumberFilter(column) ||
      this.isDropDownFilter(column)
    );
  }

  isUsernameFilter(column: TableColumn): boolean {
    return ['recipientName', 'createdBy', 'updatedBy'].includes(column.prop);
  }

  isNumberFilter(column: TableColumn): boolean {
    return column.fieldType === 'number';
  }

  isBooleanFilter(column: TableColumn): boolean {
    return column.fieldType === 'boolean';
  }

  isDropDownFilter(column: TableColumn): boolean {
    return column.fieldType === 'dropdown';
  }

  isActiveFilter(column: TableColumn) {
    return column?.appliedFilters?.length > 0;
  }

  toogleColumns(): void {
    this.columns = [...this.columns];
    this.setTableWidth();

    const columnsVisibilityMap = new Map(
      this.columns.map((column) => [
        this.getColumnProp(column),
        column.isVisible
      ])
    );
    localStorage.setItem(
      this.title,
      JSON.stringify(Array.from(columnsVisibilityMap.entries()))
    );
  }

  filterIconColor(column: TableColumn, isActive: boolean): string {
    const color =
      column.isFilterApplied && isActive
        ? this.getColumnColor(column)
        : 'inherit';

    return color;
  }

  get canExport(): boolean {
    return (
      this.exportedColumns?.length > 0 &&
      !!this.exportPermission &&
      this.appPermissionsService.has(this.exportPermission)
    );
  }

  export(): void {
    const q = this.prepareQuery(this.qb).select([
      ...this.exportedColumns,
      'createdAt'
    ]);

    this.crudService.exportData(q.query()).subscribe();
  }

  onPageSizeChange(pageSize: number = 20): void {
    this.qb.setLimit(pageSize);
    this.reload();
  }

  reload(): void {
    this.fetch(this.qb);
  }

  customSort(): void {
    this.rows = this.customSortFn(this.rows);
  }

  onRowSelect(item: T): void {
    this.selectedRow = item;
    this.rowSelected.emit(item);
  }

  private fetch(query: RequestQueryBuilder): void {
    if (this.crudService) {
      this.isLoading = true;
      this.storeAppliedFilters();

      query = this.prepareQuery(query);

      this.crudService.getMany(query.query()).subscribe(
        (pagedData) => {
          this.totalElements = pagedData.total;
          this.rows = pagedData.data.map((row) => {
            (row as T & { expand: boolean }).expand = false;
            return row;
          });
          this.pageNumber = pagedData.page;
          this.aggregatedData = pagedData.aggregatedData;
          this.isLoading = false;
        },
        (error) => {
          console.error(error);
          this.isLoading = false;
        }
      );
    } else {
      this.isLoading = false;
    }
  }

  private prepareQuery(query: RequestQueryBuilder): RequestQueryBuilder {
    if (this.parent && this.parentId) {
      query.setFilter({
        field: this.parent,
        operator: CondOperator.EQUALS,
        value: this.parentId
      });
    }
    if (this.extraParams) {
      query.queryObject.join = null;
      query.setJoin(this.extraParams);
    }

    if (this.filterParams) {
      query.setFilter(this.filterParams);
    }

    return query;
  }

  private deleteOne(itemId: number): void {
    this.isLoading = true;
    this.crudService
      .deleteOne(itemId)
      .subscribe(() => this.onSuccess(itemId), this.onError);
  }

  private onSuccess = (itemId: number) => {
    this.isLoading = false;
    this.rows = this.rows.filter((item) => item.id !== itemId);
    this.message.create('success', 'הפעולה נשמרה בהצלחה');
    this.dataChange.emit(this.rows);
  };

  private onError = (error: Error) => {
    this.isLoading = false;

    // If custom error handler provided and it handles the error, return
    if (this.customDeleteErrorHandler && this.customDeleteErrorHandler(error)) {
      return;
    }

    // Default error handling
    const errorMessage = 'הפעולה נכשלה';
    this.message.create('error', errorMessage, {
      nzDuration: 10000
    });
  };

  private sortOrder(direction: string): QuerySortOperator {
    return (direction === 'ascend' ? 'ASC' : 'DESC') as QuerySortOperator;
  }

  private sortDirection(order: string): 'ascend' | 'descend' {
    return order === 'ASC' ? 'ascend' : 'descend';
  }

  private _setAndFetchByFilters() {
    this.qb.queryObject.filter = null;
    this.qb.queryObject.or = null;
    this.qb.queryObject.page = 1;

    this.columns
      .filter((column) => column.appliedFilters)
      .forEach((column) => {
        if (this.isDropDownFilter(column)) {
          this.setFilter(
            this.getColumnProp(column),
            column.appliedFilters.map((filter: ColumnFilter) => filter.value),
            CondOperator.IN
          );
        } else if (this.isDateColumn(column)) {
          const startDate = startOfDay(
            new Date(column.appliedFilters[0] as string)
          ).toISOString();
          const endDate = endOfDay(
            new Date(column.appliedFilters[1] as string)
          ).toISOString();

          this.setFilter(
            this.getColumnProp(column),
            [startDate, endDate],
            CondOperator.BETWEEN
          );
        } else if (this.isNumberFilter(column)) {
          this.setFilter(
            this.getColumnProp(column),
            column.appliedFilters,
            CondOperator.EQUALS
          );
        } else {
          this.setFilter(
            this.getColumnProp(column),
            column.appliedFilters,
            CondOperator.CONTAINS
          );
        }
      });

    this.fetch(this.qb);
  }

  private setFilter(
    field: string,
    value: string | number | (string | number | Date | ColumnFilter)[],
    operator: CondOperator
  ) {
    if (isArray(value) && value.includes(null)) {
      value = value.filter((x) => x !== null);
      this.qb.setFilter({ field, operator: CondOperator.IS_NULL });
    }
    if (value && (value as string[]).length) {
      this.qb.setFilter({ field, operator, value });
    }
  }

  private checkQueryParams() {
    this.route.queryParams.subscribe((params) => {
      Object.keys(params).forEach((key) => {
        const column = this.columns.find((c) => key === this.getColumnProp(c));
        if (column) {
          column.appliedFilters = params[key];
        }
      });
    });
  }

  private openItem(item: T) {
    if (this.crudDialog) {
      this.crudDialog.edit({ ...item });
    } else {
      this.router.navigateByUrl(`app/${this.pageLink}/${item.id}`);
    }
  }

  private changesHandler(event: CrudItemChanges<BaseDto>) {
    if (
      event &&
      event.path === this.pageLink &&
      event.action !== ChangeType.receivedFromDB
    ) {
      this._setAndFetchByFilters();
    }
  }

  private setTableWidth(): void {
    let widthSum = 0;
    this.columns
      .filter((column) => column.isVisible)
      .forEach((column) => (widthSum += parseInt(column.width, 10) || 160));

    this.isScrollable = this.simpleTable
      ? { y: '', x: `${widthSum}px` }
      : { y: 'calc(100vh - 210px)', x: `${widthSum}px` };
  }

  private loadCustomizedData(): void {
    const columnsVisibilityMap = new Map(
      JSON.parse(localStorage.getItem(this.title))
    );
    if (columnsVisibilityMap.size > 0) {
      this.columns = this.columns.map((column) => {
        column.isVisible =
          columnsVisibilityMap.get(this.getColumnProp(column)) === false
            ? false
            : true;
        return column;
      });
    }
  }

  private storeAppliedFilters(): void {
    const appliedFilters = {};
    this.columns.forEach(
      (c) => (appliedFilters[c.name || c.prop] = c.appliedFilters)
    );
    localStorage.setItem(
      `${this.title}_appliedFilters`,
      JSON.stringify(appliedFilters)
    );
  }

  private storeAppliedSort(querySort: QuerySort): void {
    localStorage.setItem(`${this.title}_querySort`, JSON.stringify(querySort));
  }

  private loadAppliedSort(): void {
    const querySort: QuerySort = JSON.parse(
      localStorage.getItem(`${this.title}_querySort`)
    );
    if (querySort) {
      this.qb.sortBy(querySort);
      const column = this.columns.find(
        (c) => querySort.field === this.getColumnProp(c)
      );
      column.sortOrder = this.sortDirection(querySort.order);
    } else {
      const column = this.columns.find((c) => c.sortOrder);
      if (column) {
        this.qb.sortBy({
          field: this.getColumnProp(column),
          order: this.sortOrder(column?.sortOrder)
        });
      }
    }
  }

  private loadAppliedFilters(): void {
    const appliedFilters = JSON.parse(
      localStorage.getItem(`${this.title}_appliedFilters`)
    );

    if (appliedFilters) {
      this.columns.forEach((c) => {
        c.appliedFilters = appliedFilters[c.name || c.prop];
        c.isFilterApplied = !!c.appliedFilters;
      });

      this.cdRef.detectChanges();
    }
  }

  private setColors(): void {
    this.columns.forEach((column, index) => {
      this.colorsMap.set(
        this.getColumnProp(column),
        COLORS[index % COLORS.length]
      );
    });
  }
}
